import { router } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { SegmentedControl } from "@/components/segmented-control"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { brandAnalyticsMissionsPath } from "@/routes"
import {
  type AnalyticsFilters,
  type AnalyticsWindow,
  MISSION_TYPE_VALUES,
  WINDOW_VALUES,
} from "@/types/mission-analytics"

/**
 * OKS-63 — URL-stateful filter bar for the cross-mission overview.
 *
 * Two controls:
 *   - shadcn `Select` for `mission_type` (4 options: All / Drop /
 *     Promotional / Open).
 *   - shadcn `ToggleGroup` (single, segmented) for `window` (30d / 90d
 *     / all-time).
 *
 * Filter change → `router.get(brandAnalyticsMissionsPath({...}), {},
 * { only: [...], preserveState: false, preserveScroll: true })`.
 * Inertia partial reload keeps the chart + KPIs + table cards in
 * skeleton swap; full-page nav does NOT happen.
 *
 * URL is the only state — no `localStorage` (PRD §6 + Crew hard rule).
 * Refresh / back-button preserve via Inertia's URL-driven page
 * resolution.
 */
export interface AnalyticsFilterBarProps {
  filters: AnalyticsFilters
}

// Props the partial reload should refresh — matches the locked Inertia
// contract in TRD §3 + the controller's overview prop bag.
const OVERVIEW_RELOAD_ONLY = [
  "state",
  "filters",
  "kpis",
  "trend",
  "top_missions",
]

export function AnalyticsFilterBar({ filters }: AnalyticsFilterBarProps) {
  const { t } = useTranslation()

  const apply = (next: Partial<AnalyticsFilters>) => {
    const merged: AnalyticsFilters = { ...filters, ...next }
    router.get(
      brandAnalyticsMissionsPath({
        mission_type: merged.mission_type,
        window: merged.window,
      }),
      undefined,
      {
        only: OVERVIEW_RELOAD_ONLY,
        preserveState: false,
        preserveScroll: true,
      },
    )
  }

  return (
    <section
      aria-label={t("analytics.filters.section_aria")}
      data-testid="analytics-overview-filter-bar"
      className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4"
    >
      <div className="flex flex-col gap-1.5">
        <Label
          htmlFor="analytics-mission-type-select"
          className="text-muted-foreground text-xs font-medium"
        >
          {t("analytics.filters.mission_type.label")}
        </Label>
        <Select
          items={MISSION_TYPE_VALUES.map((value) => ({
            value,
            label: t(`analytics.filters.mission_type.${value}`),
          }))}
          value={filters.mission_type}
          onValueChange={(v) => v != null && apply({ mission_type: v })}
          name="mission_type"
        >
          <SelectTrigger
            id="analytics-mission-type-select"
            data-testid="analytics-overview-filter-type"
            className="min-w-[180px]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MISSION_TYPE_VALUES.map((value) => (
              <SelectItem key={value} value={value}>
                {t(`analytics.filters.mission_type.${value}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-muted-foreground text-xs font-medium">
          {t("analytics.filters.window.label")}
        </Label>
        <SegmentedControl
          value={filters.window}
          // Radio-group always carries exactly one value and never fires
          // for the active option, so no empty-string guard is needed.
          onValueChange={(v: AnalyticsWindow) => apply({ window: v })}
          variant="outline"
          size="sm"
          data-testid="analytics-overview-filter-window"
          aria-label={t("analytics.filters.window.label")}
          options={WINDOW_VALUES.map((value) => ({
            value,
            label: t(`analytics.filters.window.${value}`),
            "data-testid": `analytics-overview-filter-window-${value}`,
            "aria-label": t(`analytics.filters.window.${value}`),
          }))}
        />
      </div>
    </section>
  )
}
